import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';

class ThreeDController {
  constructor(container) {
    if (!container) {
      throw new Error('Container element is required');
    }

    this.container = container;
    this.scene = null;
    this.camera = null;
    this.defaultCamera = null;
    this.renderer = null;
    this.controls = null;
    this.model = null;
    this.edgeHelpers = [];
    this.isInitialized = false;
    this.animationFrameId = null;
    this.didAutoFrame = false;
    this.baseWhiteMaterial = null;
    this._autoRotate = true;
    this._embeddedCameraClone = null;
    this._cameraLight = null;
    this._ground = null;
    this._interiorLight = null;
    this._pmremGenerator = null;
    this._hemiLight = null;
    this._mainLight = null;
    this._fillLight = null;
    this._rimLight = null;
  }

  setLightingEnabled(enabled) {
    const on = !!enabled;
    try {
      if (this._hemiLight) this._hemiLight.intensity = on ? 0.35 : 0;
    } catch {}
    try {
      if (this._mainLight) this._mainLight.intensity = on ? 0.85 : 0;
    } catch {}
    try {
      if (this._fillLight) this._fillLight.intensity = on ? 0.6 : 0;
    } catch {}
    try {
      if (this._rimLight) this._rimLight.intensity = on ? 0.25 : 0;
    } catch {}
    try {
      if (this._cameraLight) this._cameraLight.intensity = on ? 1.75 : 0;
    } catch {}
    try {
      if (this._interiorLight) this._interiorLight.intensity = on ? 0.9 : 0;
    } catch {}

    try {
      if (this.renderer && this.renderer.shadowMap) {
        this.renderer.shadowMap.enabled = on;
      }
    } catch {}
    try {
      if (this._ground) {
        this._ground.visible = on;
      }
    } catch {}
  }

  findEmbeddedCameras(root) {
    if (!root) return [];
    const cameras = [];
    try {
      root.traverse((obj) => {
        if (!obj) return;
        if (obj.isCamera) cameras.push(obj);
      });
    } catch {}

    cameras.sort((a, b) => {
      const an = (a?.name || '').toLowerCase();
      const bn = (b?.name || '').toLowerCase();
      const aScore = an === 'camera' ? 2 : (an.includes('camera') ? 1 : 0);
      const bScore = bn === 'camera' ? 2 : (bn.includes('camera') ? 1 : 0);
      return bScore - aScore;
    });

    return cameras;
  }

  setActiveCamera(camera) {
    if (!camera || !this.renderer) return;
    this.camera = camera;

    // If we have a camera-attached light, re-parent it to the new camera.
    try {
      if (this._cameraLight) {
        try {
          if (this._cameraLight.parent) {
            this._cameraLight.parent.remove(this._cameraLight);
          }
        } catch {}
        try {
          this.camera.add(this._cameraLight);
          this._cameraLight.position.set(0, 0.15, 1.0);
        } catch {}
      }
    } catch {}

    try {
      if (this._interiorLight) {
        try {
          if (this._interiorLight.parent) {
            this._interiorLight.parent.remove(this._interiorLight);
          }
        } catch {}
        this._interiorLight = null;
      }
    } catch {}

    try {
      if (this._pmremGenerator) {
        try { this._pmremGenerator.dispose(); } catch {}
        this._pmremGenerator = null;
      }
    } catch {}

    try {
      if (this.controls) this.controls.dispose();
    } catch {}

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.autoRotate = !!this._autoRotate;
    this.controls.autoRotateSpeed = 1.0;
    this.controls.minDistance = 0.05;
    this.controls.maxDistance = 100;

    try {
      const size = new THREE.Vector2();
      this.renderer.getSize(size);
      if (this.camera && this.camera.isPerspectiveCamera) {
        this.camera.aspect = (size.x || 1) / (size.y || 1);
      }
      if (this.camera && typeof this.camera.updateProjectionMatrix === 'function') {
        this.camera.updateProjectionMatrix();
      }
    } catch {}
  }

  resetToDefaultCamera() {
    if (!this.defaultCamera) return;
    this.setActiveCamera(this.defaultCamera);
  }

  cloneEmbeddedCameraToRoot(embeddedCamera) {
    if (!embeddedCamera) return null;
    try {
      if (this._embeddedCameraClone && this._embeddedCameraClone.parent) {
        this._embeddedCameraClone.parent.remove(this._embeddedCameraClone);
      }
    } catch {}
    this._embeddedCameraClone = null;

    try {
      embeddedCamera.updateMatrixWorld(true);
    } catch {}

    try {
      const m = embeddedCamera.matrixWorld ? embeddedCamera.matrixWorld.clone() : null;
      if (!m) return null;

      let cam = null;
      if (embeddedCamera.isPerspectiveCamera) {
        let aspect = 1;
        try {
          const size = new THREE.Vector2();
          if (this.renderer) this.renderer.getSize(size);
          aspect = (size.x || 1) / (size.y || 1);
        } catch {}
        cam = new THREE.PerspectiveCamera(embeddedCamera.fov || 75, aspect, embeddedCamera.near || 0.1, embeddedCamera.far || 1000);
      } else if (embeddedCamera.isOrthographicCamera) {
        cam = new THREE.OrthographicCamera(
          embeddedCamera.left ?? -1,
          embeddedCamera.right ?? 1,
          embeddedCamera.top ?? 1,
          embeddedCamera.bottom ?? -1,
          embeddedCamera.near ?? 0.1,
          embeddedCamera.far ?? 1000
        );
      } else {
        let aspect = 1;
        try {
          const size = new THREE.Vector2();
          if (this.renderer) this.renderer.getSize(size);
          aspect = (size.x || 1) / (size.y || 1);
        } catch {}
        cam = new THREE.PerspectiveCamera(75, aspect, 0.1, 1000);
      }

      const pos = new THREE.Vector3();
      const quat = new THREE.Quaternion();
      const scale = new THREE.Vector3();
      m.decompose(pos, quat, scale);

      cam.position.copy(pos);
      cam.quaternion.copy(quat);
      try {
        if (embeddedCamera.up) cam.up.copy(embeddedCamera.up);
      } catch {}
      cam.name = embeddedCamera.name || 'EmbeddedCamera';
      try {
        cam.updateMatrixWorld(true);
      } catch {}
      try {
        cam.updateProjectionMatrix();
      } catch {}

      try {
        if (this.scene) this.scene.add(cam);
      } catch {}
      this._embeddedCameraClone = cam;
      return cam;
    } catch {
      return null;
    }
  }

  clearEdgeOutlines() {
    try {
      if (Array.isArray(this.edgeHelpers)) {
        this.edgeHelpers.forEach((h) => {
          try {
            if (h && h.parent) h.parent.remove(h);
          } catch {}
          try {
            if (h && h.geometry) h.geometry.dispose();
          } catch {}
          try {
            if (h && h.material) h.material.dispose();
          } catch {}
        });
      }
    } catch {}
    this.edgeHelpers = [];
  }

  addEdgeOutlinesToModel() {
    if (!this.model) return;
    this.clearEdgeOutlines();

    const material = new THREE.LineBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.22
    });

    this.model.traverse((child) => {
      if (!child || !child.isMesh || !child.geometry) return;
      try {
        const edges = new THREE.EdgesGeometry(child.geometry, 25);
        const line = new THREE.LineSegments(edges, material);
        line.renderOrder = 999;
        child.add(line);
        this.edgeHelpers.push(line);
      } catch {}
    });
  }

  applyBaseWhiteMaterial() {
    if (!this.model) return;
    if (!this.baseWhiteMaterial) return;
    this.model.traverse((child) => {
      if (child && child.isMesh) {
        child.material = this.baseWhiteMaterial;
      }
    });
  }

  applySolidColorMaterial(color, targetMeshName = null, options = {}) {
    if (!this.model) return;
    const mat = new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.DoubleSide
    });
    const targetLower = targetMeshName ? String(targetMeshName).toLowerCase() : null;
    const excludeMeshName = options?.excludeMeshName ? String(options.excludeMeshName).toLowerCase() : null;
    const excludeMeshMatch = options?.excludeMeshMatch === 'contains' ? 'contains' : 'exact';
    this.model.traverse((child) => {
      if (!child || !child.isMesh) return;
      const childName = (child.name || '').toLowerCase();
      if (excludeMeshName) {
        const isExcluded = excludeMeshMatch === 'contains'
          ? childName.includes(excludeMeshName)
          : (childName === excludeMeshName);
        if (isExcluded) return;
      }
      if (targetLower) {
        if (childName !== targetLower && !childName.includes(targetLower)) return;
      }
      child.material = mat;
    });
  }

  hasMeshNamedInsensitive(root, name) {
    return !!this.findObjectByNameInsensitive(root, name);
  }

  findObjectByNameContainsInsensitive(root, namePart) {
    if (!root || !namePart) return null;
    const target = String(namePart).toLowerCase();
    let found = null;
    root.traverse((obj) => {
      if (found) return;
      const n = (obj && obj.name) ? String(obj.name).toLowerCase() : '';
      if (n && n.includes(target)) {
        found = obj;
      }
    });
    return found;
  }

  hasMeshNameContainsInsensitive(root, namePart) {
    return !!this.findObjectByNameContainsInsensitive(root, namePart);
  }

  findObjectByNameInsensitive(root, name) {
    if (!root || !name) return null;
    const target = String(name).toLowerCase();
    let found = null;
    root.traverse((obj) => {
      if (found) return;
      const n = (obj && obj.name) ? String(obj.name).toLowerCase() : '';
      if (n === target) {
        found = obj;
      }
    });
    return found;
  }

  frameObject(object3d, opts = {}) {
    if (!object3d || !this.camera || !this.controls) return;

    const {
      margin = 1.35,
      useLocalFrontAxis = true,
      invertFront = false,
      minDistance = 0,
      centerOffset = null
    } = opts;

    const box = new THREE.Box3().setFromObject(object3d);
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3());
      if (centerOffset && typeof centerOffset === 'object') {
        const ox = typeof centerOffset.x === 'number' ? centerOffset.x : 0;
        const oy = typeof centerOffset.y === 'number' ? centerOffset.y : 0;
        const oz = typeof centerOffset.z === 'number' ? centerOffset.z : 0;
        center.add(new THREE.Vector3(ox, oy, oz));
      }
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);

      const fov = this.camera.fov * (Math.PI / 180);
      let distance = Math.abs(maxDim / (2 * Math.tan(fov / 2)));
      distance *= margin;
      if (typeof minDistance === 'number' && minDistance > 0) {
        distance = Math.max(distance, minDistance);
      }

      let dir = new THREE.Vector3(0, 0, 1);
      if (useLocalFrontAxis) {
        const q = new THREE.Quaternion();
        object3d.getWorldQuaternion(q);
        dir.applyQuaternion(q);
      }
      dir.normalize();
      if (invertFront) dir.multiplyScalar(-1);

      this.controls.target.copy(center);
      this.camera.position.copy(center).add(dir.multiplyScalar(distance));

      // Keep camera planes sane so the design area doesn't clip
      this.camera.near = Math.max(0.01, distance / 200);
      this.camera.far = Math.max(1000, distance * 20);
      this.camera.updateProjectionMatrix();
      this.controls.update();
    }
  }

  focusOnDesignMesh(meshName = 'Design', opts = {}) {
    if (!this.model) return;
    const target = this.findObjectByNameInsensitive(this.model, meshName);
    if (target) {
      this.frameObject(target, opts);
      return;
    }
    // Fallback: frame entire model
    this.frameObject(this.model, opts);
  }

  /**
   * Initialize the Three.js scene with configurable options
   * @param {Object} config - Configuration object
   * @param {number} config.width - Renderer width in pixels
   * @param {number} config.height - Renderer height in pixels
   * @param {string} [config.backgroundColor='#f8f9fa'] - Background color (CSS color or CSS variable, e.g. 'var(--site-canvas-bg)')
   * @param {boolean} [config.autoRotate=true] - Whether to auto-rotate the camera
   * @returns {THREE.Scene} The initialized Three.js scene
   */
  init(config) {
    try {
      // Validate config
      if (!config || typeof config !== 'object') {
        throw new Error('Configuration object is required');
      }

      const {
        width,
        height,
        backgroundColor = '#f8f9fa',
        autoRotate = true
      } = config;

      this._autoRotate = !!autoRotate;

      // Validate dimensions
      if (typeof width !== 'number' || typeof height !== 'number' ||
        width <= 0 || height <= 0) {
        throw new Error('Invalid dimensions: width and height must be positive numbers');
      }

      // Validate container
      if (!(this.container instanceof HTMLElement)) {
        throw new Error('Container must be a valid DOM element');
      }

      // Create scene
      this.scene = new THREE.Scene();
      // Keep Three.js background transparent so CSS variable on container shows through
      this.scene.background = null;

      // Create camera
      const aspectRatio = width / height;
      this.camera = new THREE.PerspectiveCamera(75, aspectRatio, 0.1, 1000);
      this.camera.position.z = 1;
      this.defaultCamera = this.camera;

      // Create renderer
      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);

      try {
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      } catch {}

      // Better output (helps PBR materials like MeshPhysicalMaterial look correct)
      try {
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
      } catch {}
      try {
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      } catch {}

      // Studio environment lighting so ceramic clearcoat has something to reflect
      try {
        if (this._pmremGenerator) {
          try { this._pmremGenerator.dispose(); } catch {}
          this._pmremGenerator = null;
        }
      } catch {}
      try {
        this._pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        this._pmremGenerator.compileEquirectangularShader();
        this.scene.environment = this._pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture;
      } catch {}
      
      // Clear container and append renderer
      while (this.container.firstChild) {
        this.container.removeChild(this.container.firstChild);
      }
      this.container.appendChild(this.renderer.domElement);

      // Also set container background so CSS variables work when renderer is transparent
      try {
        if (typeof backgroundColor === 'string') {
          this.container.style.background = backgroundColor;
        }
      } catch (e) {
        console.warn('Failed to set container background style:', e);
      }

      // Add controls
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.autoRotate = autoRotate;
      this.controls.autoRotateSpeed = 1.0;
      // Allow getting close to the model without OrbitControls pushing the camera back
      this.controls.minDistance = 0.05;
      this.controls.maxDistance = 100;

      const hemiLight = new THREE.HemisphereLight(0xeaf2ff, 0x8a7d73, 0.35);
      this.scene.add(hemiLight);
      this._hemiLight = hemiLight;

      // Add main directional light (simulates sunlight)
      const mainLight = new THREE.DirectionalLight(0xffffff, 0.85);
      mainLight.position.set(2.5, 4.0, 2.0);
      mainLight.castShadow = true;
      try {
        mainLight.shadow.mapSize.width = 2048;
        mainLight.shadow.mapSize.height = 2048;
        mainLight.shadow.camera.near = 0.1;
        mainLight.shadow.camera.far = 30;
        mainLight.shadow.camera.left = -5;
        mainLight.shadow.camera.right = 5;
        mainLight.shadow.camera.top = 5;
        mainLight.shadow.camera.bottom = -5;
        mainLight.shadow.bias = -0.00015;
        mainLight.shadow.radius = 4;
      } catch {}
      this.scene.add(mainLight);
      this._mainLight = mainLight;

      // Add fill light from opposite direction
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
      fillLight.position.set(-1, 0.5, -1);
      this.scene.add(fillLight);
      this._fillLight = fillLight;

      // Add rim light for highlighting edges
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.25);
      rimLight.position.set(0, -1, 0);
      this.scene.add(rimLight);
      this._rimLight = rimLight;

      try {
        if (this._ground && this._ground.parent) {
          this._ground.parent.remove(this._ground);
        }
      } catch {}
      try {
        const groundGeo = new THREE.PlaneGeometry(20, 20);
        const groundMat = new THREE.ShadowMaterial({ opacity: 0.22 });
        const ground = new THREE.Mesh(groundGeo, groundMat);
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.75;
        ground.receiveShadow = true;
        this.scene.add(ground);
        this._ground = ground;
      } catch {}

      // Camera-attached light: improves visibility inside concave models (e.g., mug interior)
      // without changing materials.
      try {
        if (this._cameraLight && this._cameraLight.parent) {
          this._cameraLight.parent.remove(this._cameraLight);
        }
      } catch {}
      try {
        this._cameraLight = new THREE.PointLight(0xffffff, 1.75, 12);
        this._cameraLight.position.set(0, 0.15, 1.0);
        this._cameraLight.castShadow = false;
        this.camera.add(this._cameraLight);
      } catch {}

      // Extra fill light aimed into the mug (no shadows). Helps the inner hole not look black.
      try {
        if (this._interiorLight && this._interiorLight.parent) {
          this._interiorLight.parent.remove(this._interiorLight);
        }
      } catch {}
      try {
        this._interiorLight = new THREE.PointLight(0xffffff, 0.9, 2.5);
        this._interiorLight.position.set(0, 0.9, 0);
        this._interiorLight.castShadow = false;
        this.scene.add(this._interiorLight);
      } catch {}
      
      // Enable physically correct lighting model
      this.renderer.physicallyCorrectLights = true;

      // Start animation loop
      this.animate();
      
      this.isInitialized = true;
      return this.scene;
    } catch (error) {
      console.error('Failed to initialize Three.js scene:', error);
      throw error;
    }
  }

  /**
   * Animation loop
   */
  animate = () => {
    this.animationFrameId = requestAnimationFrame(this.animate);
    
    if (this.controls) {
      this.controls.update();
    }
    
    if (this.renderer && this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }
  };

  /**
   * Load a 3D model (FBX)
   * @param {string} url - Model URL
   * @param {function|Object} [onLoadedOrOptions] - Either an onLoaded callback or options object { onLoaded }
   * @returns {Promise<THREE.Object3D>} resolves with loaded model
   */
  loadModel(url, onLoadedOrOptions) {
    let onLoaded = null;
    let options = {};
    if (typeof onLoadedOrOptions === 'function') {
      onLoaded = onLoadedOrOptions;
    } else if (typeof onLoadedOrOptions === 'object') {
      options = onLoadedOrOptions;
      if (options.onLoaded && typeof options.onLoaded === 'function') {
        onLoaded = options.onLoaded;
      }
    }
    const materialType = options.materialType || null;
    this._currentMaterialType = materialType;
    const baseColor = options.baseColor || null;
    // Lighting/shadows: only enabled for Ceramic mode
    try {
      this.setLightingEnabled(materialType === 'Ceramic');
    } catch {}
    // console.log('Loading model from:', url);
    
    // If no URL or placeholder is specified, create a simple cube
    if (!url || url === 'placeholder') {
      // Create a placeholder cube
      const geometry = new THREE.BoxGeometry(1, 1, 1);
      const material = new THREE.MeshStandardMaterial({ color: 0x00ff00 });
      this.model = new THREE.Mesh(geometry, material);
      this.scene.add(this.model);
      return Promise.resolve(this.model);
    }
    
    // Remove any existing model
    if (this.model) {
      this.clearEdgeOutlines();
      this.scene.remove(this.model);
      this.model = null;
    }

    try {
      this.resetToDefaultCamera();
    } catch {}

    // Reset auto-framing for the next model
    this.didAutoFrame = false;
    
    // Determine file type from URL, removing any query parameters first
    const urlWithoutParams = url.split('?')[0];
    const fileExtension = urlWithoutParams.split('.').pop().toLowerCase();
    // console.log('File extension detected:', fileExtension);
    
    return new Promise((resolve, reject) => {
      // Handle FBX files
      if (fileExtension === 'fbx') {
        // console.log('Detected FBX file, attempting to load from URL:', url);
        
        // Check if URL is accessible with a HEAD request
        fetch(url, { method: 'HEAD' })
          .then(response => {
            if (!response.ok) {
              console.error(`FBX URL returned status ${response.status}:`, url);
              throw new Error(`FBX URL returned status ${response.status}`);
            }
            // console.log('FBX URL is accessible, proceeding with load');
            
            const loader = new FBXLoader();
            loader.load(
              url,
              (fbx) => {
                // Basic, robust FBX setup: scale, center, apply ceramic material, enable shadows.
                try {
                  fbx.scale.set(0.01, 0.01, 0.01);
                } catch {}

                try {
                  fbx.updateMatrixWorld(true);
                } catch {}

                // Copy FBX embedded camera settings onto our existing camera
                try {
                  const cams = this.findEmbeddedCameras(fbx);
                  const fbxCam = (cams && cams.length) ? cams[0] : null;
                  if (fbxCam && this.camera) {
                    try { fbxCam.updateMatrixWorld(true); } catch {}

                    const wp = new THREE.Vector3();
                    const wq = new THREE.Quaternion();
                    try {
                      fbxCam.getWorldPosition(wp);
                      fbxCam.getWorldQuaternion(wq);
                    } catch {}

                    try {
                      this.camera.position.copy(wp);
                      this.camera.quaternion.copy(wq);
                    } catch {}

                    try {
                      if (materialType === 'Ceramic') {
                        if (fbxCam.isPerspectiveCamera && this.camera.isPerspectiveCamera) {
                          this.camera.fov = fbxCam.fov;
                          this.camera.near = fbxCam.near;
                          this.camera.far = fbxCam.far;
                        } else if (fbxCam.isOrthographicCamera && this.camera.isOrthographicCamera) {
                          this.camera.left = fbxCam.left;
                          this.camera.right = fbxCam.right;
                          this.camera.top = fbxCam.top;
                          this.camera.bottom = fbxCam.bottom;
                          this.camera.near = fbxCam.near;
                          this.camera.far = fbxCam.far;
                        }
                      }
                    } catch {}

                    try {
                      if (typeof this.camera.updateProjectionMatrix === 'function') {
                        this.camera.updateProjectionMatrix();
                      }
                    } catch {}

                    console.log('[FBX Camera -> App Camera] Applied FBX camera transform' + (materialType === 'Ceramic' ? ' + projection' : '')); 
                    console.log('[FBX Camera] source:', fbxCam);
                    console.log('[App Camera] target:', this.camera);
                  } else {
                    console.warn('No embedded FBX camera found to apply, or app camera is missing');
                  }
                } catch {}

                try {
                  const box = new THREE.Box3().setFromObject(fbx);
                  if (!box.isEmpty()) {
                    const center = box.getCenter(new THREE.Vector3());
                    fbx.position.sub(center);
                  }
                } catch {}

                let ceramicMaterial = null;
                let baseColorMaterial = null;
                try {
                  if (materialType === 'Ceramic') {
                    ceramicMaterial = new THREE.MeshPhysicalMaterial({
                      color: 0xffffff,
                      metalness: 0.0,
                      roughness: 0.35,
                      clearcoat: 1.0,
                      clearcoatRoughness: 0.12,
                      envMapIntensity: 0.9,
                      side: THREE.DoubleSide
                    });
                    this.baseWhiteMaterial = ceramicMaterial;
                  } else {
                    this.baseWhiteMaterial = null;
                    if (baseColor != null && baseColor !== '') {
                      try {
                        baseColorMaterial = new THREE.MeshBasicMaterial({
                          color: baseColor,
                          side: THREE.DoubleSide
                        });
                      } catch {}
                    }
                  }
                } catch {
                  ceramicMaterial = null;
                  this.baseWhiteMaterial = null;
                }

                try {
                  fbx.traverse((child) => {
                    if (!child || !child.isMesh) return;
                    child.userData.originalMaterial = child.material;
                    const childName = (child.name || '').toLowerCase();
                    const isDesign = childName.includes('design');
                    if (ceramicMaterial && !isDesign) {
                      child.material = ceramicMaterial;
                    } else if (baseColorMaterial && !isDesign) {
                      child.material = baseColorMaterial;
                    }
                    try {
                      const lit = materialType === 'Ceramic';
                      child.castShadow = lit;
                      child.receiveShadow = lit;
                    } catch {}
                  });
                } catch {}

                this.model = fbx;
                this.scene.add(this.model);

                // Ceramic mode can apply an embedded FBX camera with bad near/far or position.
                // Always auto-frame after load to avoid the model being clipped/off-screen.
                try {
                  if (materialType === 'Ceramic' && this.camera && this.controls && this.model) {
                    this.frameObject(this.model, {
                      margin: 1.35,
                      useLocalFrontAxis: false,
                      invertFront: false,
                      minDistance: 0.15
                    });
                  }
                } catch {}

                // Ground plane should sit under the model.
                try {
                  if (this._ground) {
                    const b = new THREE.Box3().setFromObject(this.model);
                    if (!b.isEmpty()) {
                      this._ground.position.y = b.min.y - 0.01;
                    }
                  }
                } catch {}

                // Place interior fill light near the mug opening so it actually lights the inside.
                try {
                  if (this._interiorLight) {
                    const b = new THREE.Box3().setFromObject(this.model);
                    if (!b.isEmpty()) {
                      this._interiorLight.position.set(0, b.max.y - 0.15, 0);
                    }
                  }
                } catch {}

                // Default: no edge outlines
                try { this.clearEdgeOutlines(); } catch {}

                if (onLoaded && typeof onLoaded === 'function') {
                  onLoaded(this.model);
                }

                resolve(this.model);
              },
              // Progress callback
              (xhr) => {
                const percent = xhr.total ? (xhr.loaded / xhr.total) * 100 : 0;
                // console.log(`Loading FBX: ${percent.toFixed(2)}% (${xhr.loaded} bytes)`);
              },
              // Error callback
              (error) => {
                console.error('Error loading FBX model:', error);
                // Create a placeholder cube as fallback
                // console.log('Creating placeholder cube due to FBX load error');
                const geometry = new THREE.BoxGeometry(1, 1, 1);
                const material = new THREE.MeshStandardMaterial({ color: 0xff0000 }); // Red cube for error
                this.model = new THREE.Mesh(geometry, material);
                this.scene.add(this.model);
                reject(error);
              }
            );
          })
          .catch(error => {
            console.error('Error checking FBX URL:', error);
            // Create a placeholder cube as fallback
            // console.log('Creating placeholder cube due to URL access error');
            const geometry = new THREE.BoxGeometry(1, 1, 1);
            const material = new THREE.MeshStandardMaterial({ color: 0xff0000 }); // Red cube for error
            this.model = new THREE.Mesh(geometry, material);
            this.scene.add(this.model);
            reject(error);
          });
      } else {
        // For other file types or unknown extensions, create a placeholder
        console.warn(`Unsupported file type: ${fileExtension}. Using placeholder.`);
        const geometry = new THREE.BoxGeometry(1, 1, 1);
        const material = new THREE.MeshStandardMaterial({ color: 0x00ff00 });
        this.model = new THREE.Mesh(geometry, material);
        this.scene.add(this.model);
        resolve(this.model);
      }
    });
  }

  /**
   * Resize the renderer when the container size changes
   * @param {number} width - New width in pixels
   * @param {number} height - New height in pixels
   */
  resize(width, height) {
    if (!this.isInitialized) return;
    const safeW = (typeof width === 'number' && width > 0) ? width : 1;
    const safeH = (typeof height === 'number' && height > 0) ? height : 1;
    
    if (this.camera) {
      try {
        if (this.camera.isPerspectiveCamera) {
          this.camera.aspect = safeW / safeH;
        }
        this.camera.updateProjectionMatrix();
      } catch {}
    }
    
    if (this.renderer) {
      this.renderer.setSize(safeW, safeH);
    }
  }

  /**
   * Load a texture from a URL and apply it to the model
   * @param {string} textureUrl - URL to the texture image
   * @param {Object} options - Options for texture mapping
   * @param {boolean} [useFallback=true] - Whether to use fallback texture on error
   * @returns {Promise} Promise that resolves when the texture is loaded and applied
   */
  loadTexture(textureUrl, options = {}, useFallback = true) {
    // Only log a preview of the URL if it's very long (like base64)
    if (textureUrl && textureUrl.length > 100) {
      // console.log('Loading texture from:', textureUrl.substring(0, 50) + '... (truncated)');
    } else {
      // console.log('Loading texture from:', textureUrl);
    }
    
    if (!this.model) {
      console.warn('No model available to apply texture to');
      return Promise.reject(new Error('No model available'));
    }
    
    if (!textureUrl) {
      console.warn('No texture URL provided');
      return Promise.reject(new Error('No texture URL'));
    }
    
    return new Promise((resolve, reject) => {
      // Special handling for base64 data URLs
      if (textureUrl.startsWith('data:')) {
        // console.log('Processing base64 data URL...');
        // For base64, we don't need to validate it as a URL or add timestamp
      } else {
        // Validate URL before attempting to load (only for non-data URLs)
        try {
          new URL(textureUrl);
        } catch (e) {
          console.error('Invalid texture URL:', textureUrl);
          if (useFallback) {
            return this.loadFallbackTexture(options)
              .then(resolve)
              .catch(reject);
          }
          return reject(new Error('Invalid texture URL'));
        }
      }
      
      // Create a texture loader
      const textureLoader = new THREE.TextureLoader();
      
      // Set cross-origin to anonymous to allow loading from other domains
      textureLoader.crossOrigin = 'anonymous';
      
      // For non-data URLs, add timestamp to prevent caching
      let finalUrl = textureUrl;
      if (!textureUrl.startsWith('data:')) {
        const timestamp = new Date().getTime();
        finalUrl = textureUrl.includes('?') 
          ? `${textureUrl}&t=${timestamp}` 
          : `${textureUrl}?t=${timestamp}`;
      }
      
      // Function to load texture using Three.js TextureLoader
      const loadTextureWithLoader = () => {
        textureLoader.load(
          finalUrl,
          (texture) => {
            // console.log('Texture loaded successfully');
            
            // Apply texture to the model
            this.applyTextureToModel(texture, options);
            resolve(texture);
          },
          // Progress callback
          (xhr) => {
            const percent = xhr.total ? (xhr.loaded / xhr.total) * 100 : 0;
            // console.log(`Loading texture: ${percent.toFixed(2)}%`);
          },
          // Error callback
          (error) => {
            console.error('Error loading texture:', error);
            
            if (useFallback) {
              // console.log('Attempting to load fallback texture...');
              this.loadFallbackTexture(options)
                .then(resolve)
                .catch(reject);
            } else {
              reject(error);
            }
          }
        );
      };
      
      // Try to preload the image for base64 data URLs to verify it's valid
      if (textureUrl.startsWith('data:')) {
        // console.log('Pre-validating base64 image data...');
        const img = new Image();
        img.onload = () => {
          // console.log('Base64 image validated successfully, dimensions:', img.width, 'x', img.height);
          // Now load it as a texture
          loadTextureWithLoader();
        };
        img.onerror = (err) => {
          console.error('Invalid base64 image data:', err);
          if (useFallback) {
            this.loadFallbackTexture(options)
              .then(resolve)
              .catch(reject);
          } else {
            reject(new Error('Invalid base64 image data'));
          }
        };
        img.src = textureUrl;
      } else {
        // For regular URLs, load directly
        loadTextureWithLoader();
      }
    });
  }
  
  /**
   * Load a fallback texture when the main texture fails to load
   * @param {Object} options - Options for texture mapping
   * @returns {Promise} Promise that resolves when the fallback texture is loaded and applied
   */
  loadFallbackTexture(options = {}) {
    return new Promise((resolve, reject) => {
      // Create a simple canvas texture as fallback
      const canvas = document.createElement('canvas');
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext('2d');
      
      // Create a gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#f5f5f5');
      gradient.addColorStop(1, '#e0e0e0');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add a border
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 8;
      ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
      
      // Add text
      ctx.fillStyle = '#666666';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Design Texture', canvas.width / 2, canvas.height / 2 - 20);
      ctx.font = '24px Arial';
      ctx.fillText('(Fallback)', canvas.width / 2, canvas.height / 2 + 20);
      
      // Create texture from canvas
      const texture = new THREE.CanvasTexture(canvas);
      
      // Apply texture to the model
      this.applyTextureToModel(texture, options);
      resolve(texture);
    });
  }
  
  /**
   * Apply a loaded texture to the model
   * @param {THREE.Texture} texture - The loaded texture
   * @param {Object} options - Options for texture mapping
   */
  applyTextureToModel(texture, options = {}) {
    if (!this.model) {
      console.warn('No model to apply texture to');
      return;
    }
    
    // console.log('Applying texture to model');

    const forceNonTargetWhite = options?.forceNonTargetWhite === true;

    const enableAlpha = options?.enableAlpha === true;

    const targetMeshMatch = options?.targetMeshMatch === 'contains' ? 'contains' : 'exact';

    const excludeMeshName = options?.excludeMeshName ? String(options.excludeMeshName).toLowerCase() : null;
    const excludeMeshMatch = options?.excludeMeshMatch === 'contains' ? 'contains' : 'exact';

    let targetMeshName = options?.targetMeshName ? String(options.targetMeshName) : null;
    if (targetMeshName) {
      const hasTarget = targetMeshMatch === 'contains'
        ? this.hasMeshNameContainsInsensitive(this.model, targetMeshName)
        : this.hasMeshNamedInsensitive(this.model, targetMeshName);
      if (!hasTarget) {
        // If caller asked for a specific target but we can't find it, do nothing (safe).
        return;
      } else {
        // console.log(`[ThreeDController] Applying texture only to mesh name: ${targetMeshName}`);
      }
    }
    
    texture.rotation = 0;
    texture.center.set(0.5, 0.5);
    texture.flipY = false;
    
    // Set texture properties based on options
    if (options.repeat) {
      texture.repeat.set(options.repeat.x || 1, options.repeat.y || 1);
    }
    
    if (options.offset) {
      texture.offset.set(options.offset.x || 0, options.offset.y || 0);
    }
    
    texture.wrapS = options.wrapS || THREE.RepeatWrapping;
    texture.wrapT = options.wrapT || THREE.RepeatWrapping;
    
    // Apply the texture to all materials in the model
    this.model.traverse((child) => {
      if (child.isMesh && child.material) {
        const childName = (child.name || '').toLowerCase();
        if (excludeMeshName) {
          const isExcluded = excludeMeshMatch === 'contains'
            ? childName.includes(excludeMeshName)
            : (childName === excludeMeshName);
          if (isExcluded) return;
        }

        if (targetMeshName) {
          const targetLower = targetMeshName.toLowerCase();

          // By default, preserve non-target mesh materials (so variant base textures remain)
          const isTarget = targetMeshMatch === 'contains'
            ? childName.includes(targetLower)
            : (childName === targetLower);
          if (!isTarget) {
            if (forceNonTargetWhite) {
              if (this.baseWhiteMaterial) {
                child.material = this.baseWhiteMaterial;
              } else {
                child.material = new THREE.MeshStandardMaterial({
                  color: 0xffffff,
                  roughness: 0.3,
                  metalness: 0.2,
                  side: THREE.DoubleSide
                });
              }
            }
            return;
          }
        }
        // console.log('Applying texture to mesh:', child.name || 'unnamed');
        
        const isDesignMesh = (targetMeshName && String(targetMeshName).toLowerCase().includes('design')) || childName.includes('design');

        const useUnlit = this._currentMaterialType !== 'Ceramic';

        const makeLitMaterialWithMap = () => {
          if (useUnlit) {
            const m = new THREE.MeshBasicMaterial({
              color: 0xffffff,
              side: THREE.DoubleSide
            });
            m.map = texture;
            m.needsUpdate = true;
            return m;
          }
          // Keep ceramic look if present
          if (this.baseWhiteMaterial && typeof this.baseWhiteMaterial.clone === 'function') {
            const m = this.baseWhiteMaterial.clone();
            m.map = texture;
            m.needsUpdate = true;
            return m;
          }
          const m = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.6,
            metalness: 0.0,
            side: THREE.DoubleSide
          });
          m.map = texture;
          m.needsUpdate = true;
          return m;
        };

        const makeDesignCeramicMaterialWithMap = () => {
          if (useUnlit) {
            const m = new THREE.MeshBasicMaterial({
              color: 0xffffff,
              side: THREE.DoubleSide
            });
            m.map = texture;

            if (enableAlpha) {
              m.transparent = true;
              m.alphaTest = 0.01;
              m.depthWrite = false;
              m.polygonOffset = false;
            }

            m.needsUpdate = true;
            return m;
          }
          let m = null;
          try {
            if (this.baseWhiteMaterial && typeof this.baseWhiteMaterial.clone === 'function') {
              m = this.baseWhiteMaterial.clone();
            }
          } catch {}

          // If we're not in Ceramic mode and we don't have a baseWhiteMaterial,
          // do NOT force a MeshPhysicalMaterial (it makes paper/card look like ceramic).
          if (!m) {
            if (this._currentMaterialType === 'Ceramic') {
              m = new THREE.MeshPhysicalMaterial({
                color: 0xffffff,
                metalness: 0.0,
                roughness: 0.35,
                clearcoat: 1.0,
                clearcoatRoughness: 0.12,
                envMapIntensity: 0.9,
                side: THREE.DoubleSide
              });
            } else {
              m = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                roughness: 0.9,
                metalness: 0.0,
                side: THREE.DoubleSide
              });
            }
          }

          m.map = texture;

          if (enableAlpha) {
            // Cutout alpha for transparency on the Design mesh
            m.transparent = true;
            m.alphaTest = 0.01;

            // Keep it as an overlay so it doesn't fight with the base mug surface
            m.depthWrite = false;
            m.polygonOffset = false;
          }

          m.needsUpdate = true;
          return m;
        };

        // If the material is an array, apply to all materials
        if (Array.isArray(child.material)) {
          child.material = child.material.map(() => (isDesignMesh ? makeDesignCeramicMaterialWithMap() : makeLitMaterialWithMap()));
        } else {
          child.material = isDesignMesh ? makeDesignCeramicMaterialWithMap() : makeLitMaterialWithMap();
        }
      }
    });
    
    // Final pass to ensure 100% same material settings and color space for design and product
    this.model.traverse((child) => {
      if (child.isMesh && child.material) {
        const applyConsistentSettings = (mat) => {
          try {
            mat.toneMapped = this._currentMaterialType === 'Ceramic';
            mat.emissiveIntensity = 0;
            if (mat.map) {
              mat.map.colorSpace = THREE.SRGBColorSpace;
            }
            if (mat.emissiveMap) {
              mat.emissiveMap = null;
            }
            mat.needsUpdate = true;
          } catch {}
        };
        if (Array.isArray(child.material)) {
          child.material.forEach(applyConsistentSettings);
        } else {
          applyConsistentSettings(child.material);
        }
      }
    });
    
    // console.log('Texture applied to model');
  }

  /**
   * Clean up resources when the controller is no longer needed
   */
  dispose() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    
    if (this.controls) {
      this.controls.dispose();
    }
    
    if (this.renderer) {
      this.renderer.dispose();
    }

    try {
      if (this._cameraLight) {
        try {
          if (this._cameraLight.parent) {
            this._cameraLight.parent.remove(this._cameraLight);
          }
        } catch {}
        this._cameraLight = null;
      }
    } catch {}

    try {
      if (this._ground) {
        try {
          if (this._ground.parent) {
            this._ground.parent.remove(this._ground);
          }
        } catch {}
        try {
          if (this._ground.geometry) this._ground.geometry.dispose();
        } catch {}
        try {
          if (this._ground.material) this._ground.material.dispose();
        } catch {}
        this._ground = null;
      }
    } catch {}

    this.clearEdgeOutlines();
    
    // Clear the container
    while (this.container && this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    
    this.isInitialized = false;
  }
  
  /**
   * Export product design, preview, and texture images
   * @param {Object|boolean|function} options - Either an options object, a legacy boolean for fixDesign, or an onProgress function
   *   - options.fixDesign {boolean}: include product image in design export (false = transparent background)
   *   - options.onProgress {function(percent:number, message?:string)}: progress callback
   * @returns {Promise<Object>} Promise that resolves with design, preview, and texture URLs
   */
  async exportProduct(options = {}) {
    // Back-compat parsing similar to KonvaController
    let onProgress = null;
    let fixDesign = false;
    if (typeof options === 'function') {
      onProgress = options;
    } else if (typeof options === 'boolean') {
      fixDesign = options;
    } else if (options && typeof options === 'object') {
      onProgress = options.onProgress || null;
      fixDesign = !!options.fixDesign;
    }

    // console.log(`exportProduct called with fixDesign: ${fixDesign}`);
    
    if (!this.renderer || !this.scene || !this.camera || !this.model) {
      console.error('Cannot export: renderer, scene, camera, or model is not initialized');
      return null;
    }
    
    try {
      if (onProgress) onProgress(20, 'status.exportingPreview');
      // Capture the current state of the scene for the preview image
      this.renderer.render(this.scene, this.camera);
      const previewUrl = this.renderer.domElement.toDataURL('image/png');
      
      if (onProgress) onProgress(60, 'status.exportingDesign');
      // Decide design image after texture extraction (use texture if fixDesign=false)
      let designUrl = null;
      
      if (onProgress) onProgress(80, 'status.extractingTexture');
      // Extract texture from the model for the texture image
      let textureUrl = null;
      if (this.model) {
        // Find the first mesh with a texture map
        let textureFound = false;
        this.model.traverse((child) => {
          if (!textureFound && child.isMesh && child.material) {
            const material = Array.isArray(child.material) ? child.material[0] : child.material;
            
            if (material && material.map) {
              // console.log('Found texture in model');
              
              // Create a canvas to draw the texture
              const canvas = document.createElement('canvas');
              const textureSize = 1024; // Use a reasonable size for the texture export
              canvas.width = textureSize;
              canvas.height = textureSize;
              const ctx = canvas.getContext('2d');
              
              // Create an image from the texture
              const image = material.map.image;
              if (image) {
                // Draw the texture to the canvas, preserving aspect ratio
                const aspectRatio = image.width / image.height;
                let drawWidth, drawHeight, offsetX, offsetY;
                
                if (aspectRatio >= 1) {
                  // Width is larger or equal to height
                  drawWidth = textureSize;
                  drawHeight = textureSize / aspectRatio;
                  offsetX = 0;
                  offsetY = (textureSize - drawHeight) / 2;
                } else {
                  // Height is larger than width
                  drawHeight = textureSize;
                  drawWidth = textureSize * aspectRatio;
                  offsetX = (textureSize - drawWidth) / 2;
                  offsetY = 0;
                }
                
                ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
                textureUrl = canvas.toDataURL('image/png');
                // console.log('Texture image captured');
                textureFound = true;
              } else {
                console.warn('Texture image not available');
              }
            }
          }
        });
        
        // If no texture was found, create a fallback texture
        if (!textureUrl) {
          console.log('No texture found in model, creating fallback');
          const canvas = document.createElement('canvas');
          const size = 1024;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#cccccc';
          ctx.fillRect(0, 0, size, size);
          ctx.fillStyle = '#666666';
          ctx.font = '20px sans-serif';
          ctx.fillText('No texture available', 20, size / 2);
          textureUrl = canvas.toDataURL('image/png');
        }
      }
      
      // Choose design: transparent texture if fixDesign=false and texture available, else preview
      if (!designUrl) {
        designUrl = (!fixDesign && textureUrl) ? textureUrl : previewUrl;
      }

      if (onProgress) onProgress(100, 'status.exportComplete');
      // Return all images
      return { design: designUrl, preview: previewUrl, texture: textureUrl };
    } catch (error) {
      console.error('Error exporting 3D product:', error);
      if (onProgress) onProgress(100, 'error.exportFailed');
      return null;
    }
  }
}

export default ThreeDController;
